<template>
  <RexPdfGenerator
    @pdfContentGenerate="generatePdfContent"
    ref="pdfGenerator"
    :downloadLabel="$t('dbm.downloadReport')"
    :downloadVariant="getDownloadBtnVarient"
    :fileName="fileName"
    :title="$t('dbm.queryDetails')"
    :reportPath="getReportPath"
    :loading="loading"
    :loadPdfData="getQueryPerformanceTableData"
    :fetchApiError="fetchApiError"
    width="15rem"
  />
</template>

<script>
import RexPdfGenerator from '@/rex-components/rex-pdf-generator.vue';
import 'jspdf-autotable';
import { mapState, mapGetters, mapActions } from 'vuex';
import { formatDate } from '@/utils/time/formatDate';
import {
  timeStamp,
  queryTimePattern,
  metricTypes,
  DatabaseTypes,
} from '@/components/Database/database.types';
import {
  pdfConfig,
  pdfPageSize,
  modifyPdfTableStyle,
  adjustColumnStyles,
  adjustPdfTableHeader,
} from '@/utils/pdfUtils';

export default {
  components: {
    RexPdfGenerator,
  },
  props: {
    headers: { type: Array, required: true },
    fileName: { type: String, required: true },
    queryTimeThreshold: { type: Object, default: () => {} },
    disabled: { type: Boolean, default: false },
    sortCriteria: { type: Array, default: () => [] },
  },
  data() {
    return {
      queryItemsApp: [],
      loading: false,
      fetchApiError: false,
    };
  },
  computed: {
    ...mapState('globalState/settings', ['timezoneoffset']),
    ...mapState('databaseState/databaseCommonState', [
      'currentDatabaseType',
      'currentDatabaseService',
    ]),
    ...mapState('databaseState/detail', ['currentDatabaseInstance']),
    ...mapGetters('globalState/settings', ['getDurationTime']),
    getQueryTableData() {
      return this.queryItemsApp.map((item) => {
        return this.headers.map((header) => {
          if (header.key === timeStamp) {
            return formatDate(
              item[header.key],
              this.timezoneoffset,
              queryTimePattern,
            );
          } else if (header.key === 'value') {
            return this.formatValue(item[header.key]);
          }
          return item[header.key];
        });
      });
    },
    getReportPath() {
      return `${this.currentDatabaseType.label} > ${this.currentDatabaseService.label} > ${this.currentDatabaseInstance.label}`;
    },
    getDownloadBtnVarient() {
      return this.currentDatabaseService.key &&
        this.currentDatabaseInstance.key &&
        !this.disabled
        ? 'secondary'
        : 'disabled';
    },
  },
  methods: {
    ...mapActions('databaseState/databaseCommonState', [
      'GET_ALL_QUERY_RECORDS',
      'GET_QUERY_PERFORMANCE_DATA',
    ]),
    formatValue(value) {
      const numericValue = Number(value);
      return Number.isInteger(numericValue)
        ? numericValue
        : parseFloat(numericValue).toFixed(2);
    },
    async getQueryPerformanceTableData() {
      try {
        this.loading = true;
        this.fetchApiError = false;
        if (this.currentDatabaseService.key) {
          const params = {
            condition: {
              name: metricTypes.DBM_QUERIES,
              parentService: this.currentDatabaseInstance.key,
              scope: 'ServiceInstance',
              order: 'DES',
              normal: false,
              searchKeyword: '*',
              queryTimeThreshold: this.queryTimeThreshold.key,
            },
            duration: this.getDurationTime,
            pagination: {
              pageNum: 1,
              pageSize: pdfPageSize,
              needTotal: true,
            },
            sortCriteria: this.sortCriteria,
          };
          const queryData =
            this.currentDatabaseType.key === DatabaseTypes.my
              ? await this.GET_QUERY_PERFORMANCE_DATA(params)
              : await this.GET_ALL_QUERY_RECORDS(params);
          this.queryItemsApp = Array.isArray(queryData.records)
            ? queryData.records
            : [];
        } else {
          this.queryItemsApp = [];
        }
        this.loading = false;
      } catch (error) {
        this.fetchApiError = true;
        this.queryItemsApp = [];
        this.loading = false;
      }
    },
    generatePdfContent(doc) {
      doc.autoTable({
        ...pdfConfig,
        head: [adjustPdfTableHeader(this.headers)],
        body: this.getQueryTableData,
        columnStyles: adjustColumnStyles(this.headers),
        didDrawCell: (data) => modifyPdfTableStyle(data, doc),
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
